import { useRef, useState } from 'react';
import { FiPause, FiPlay } from 'react-icons/fi';

// iOS uploads photos as HEIC, which browsers can't render. Ask Cloudinary to
// serve a browser-friendly format (f_auto) for both the inline <img> and the
// full-size link, so existing + future HEIC photos display on the web.
export const toDisplayImageUrl = (url) =>
  url && url.includes('/image/upload/')
    ? url.replace('/image/upload/', '/image/upload/f_auto,q_auto/')
    : url;

const MEDIA_TYPES = ['image', 'video', 'audio', 'gif'];
export const isMediaMessage = (msg) => MEDIA_TYPES.includes(msg.type) && !!msg.mediaUrl;

export const formatClock = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// A gentle placeholder shape for voice messages without a stored waveform.
const FALLBACK_WAVE = Array.from({ length: 40 }, (_, i) => 0.35 + 0.3 * Math.abs(Math.sin(i * 0.9)));

// Box that keeps the media's shape while it loads (no layout jump).
const ratioStyle = (media, maxW) => {
  const w = media && media.width;
  const h = media && media.height;
  if (!w || !h) return { maxWidth: maxW };
  const width = Math.min(maxW, w);
  return { width, aspectRatio: `${w} / ${h}` };
};

export function VoiceMessage({ src, durationSec, waveform, pending }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(durationSec || 0);
  const bars = waveform && waveform.length ? waveform : FALLBACK_WAVE;
  const progress = duration ? current / duration : 0;

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => setPlaying(false));
    else a.pause();
  };
  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * duration;
    setCurrent(a.currentTime);
  };

  return (
    <div className="voice-msg">
      <button
        type="button"
        className="voice-play"
        onClick={toggle}
        disabled={pending}
        aria-label={playing ? 'Pause voice message' : 'Play voice message'}
      >
        {playing ? <FiPause /> : <FiPlay />}
      </button>
      <div className="voice-wave" onClick={seek} role="presentation">
        {bars.map((b, i) => (
          <span
            key={i}
            className={(i + 0.5) / bars.length <= progress ? 'on' : ''}
            style={{ height: `${Math.round(18 + b * 82)}%` }}
          />
        ))}
      </div>
      <span className="voice-time">{formatClock(playing || current ? current : duration)}</span>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
        }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setDuration(d);
        }}
      />
    </div>
  );
}

/** The media part of a chat bubble (photo, video, voice message or GIF). */
export function MessageMedia({ msg }) {
  const pending = msg.status === 'sending';
  const media = msg.media || {};
  let body = null;
  if (msg.type === 'image') {
    const src = pending ? msg.mediaUrl : toDisplayImageUrl(msg.mediaUrl);
    body = (
      <a href={src} target="_blank" rel="noopener noreferrer" className="chat-media-link">
        <img src={src} alt="Photo" className="chat-image" style={ratioStyle(media, 240)} />
      </a>
    );
  } else if (msg.type === 'video') {
    body = (
      <video
        className="chat-video"
        src={msg.mediaUrl}
        poster={media.posterUrl}
        controls={!pending}
        preload="metadata"
        playsInline
        style={ratioStyle(media, 260)}
      />
    );
  } else if (msg.type === 'gif') {
    body = <img src={msg.mediaUrl} alt="GIF" className="chat-gif" loading="lazy" style={ratioStyle(media, 220)} />;
  } else if (msg.type === 'audio') {
    body = (
      <VoiceMessage
        src={msg.mediaUrl}
        durationSec={media.durationSec}
        waveform={media.waveform}
        pending={pending}
      />
    );
  }
  return (
    <div className={`chat-media chat-media-${msg.type}`}>
      {body}
      {pending && msg.type !== 'audio' && (
        <div className="chat-media-pending">
          <span className="chat-spinner" />
        </div>
      )}
    </div>
  );
}
